import React, { useCallback, useEffect, useRef, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import { Broom, Package, Storefront, Users } from '@phosphor-icons/react'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { fetchRbacAssignmentOptions, fetchRbacQualifierOptions } from '../services/rbacApi'
import { ASSIGNED_RESOURCES, emptyAssignedData, hasAnyAssignedData } from '../utils/assignedData'
import AppChip from '../../components/chips/AppChip'
import GuidanceNote from '../../components/GuidanceNote'
import { guidanceNoteErrorColors } from '../../constants/dialogSx'
import LoadingSpinner from '../../components/LoadingSpinner'
import RbacAssignedDataCard from './RbacAssignedDataCard'
import RbacAssignmentSearch from './shared/RbacAssignmentSearch'
import useQualifierReconciliation from './shared/useQualifierReconciliation'
import '../../css/rbac/rbacAssignments.scss'
import {
  UI_COLOR_DANGER,
  UI_COLOR_RBAC_ROLE_BG,
  UI_COLOR_RBAC_ROLE_TEXT,
  UI_COLOR_PRIMARY_HOVER,
  UI_COLOR_SECONDARY_MAIN,
  UI_COLOR_SECONDARY_MID,
  UI_COLOR_SURFACE_600,
  UI_COLOR_SURFACE_900,
  UI_COLOR_SURFACE_INFO,
  UI_COLOR_TEXT_INFO
} from '../../constants/uiColors'

const TITLES = {
  products: 'Datos de producto',
  clients: 'Datos de cliente',
  app_users: 'Usuarios de Ubiqua App asignados'
}
const QUALIFIER_NUMBERS = [2, 3, 4, 5, 6, 7]
const qualifierKey = (number) => `qualifier_${number}`
const RESOURCE_ICONS = { products: Package, clients: Storefront, app_users: Users }

function AssignmentRetryNote({
  title = 'No se pudieron cargar las opciones',
  message,
  onRetry,
  disabled
}) {
  return (
    <GuidanceNote
      icon={<ErrorOutlineOutlinedIcon />}
      title={title}
      message={message}
      action={
        <Button size="small" disabled={disabled} onClick={onRetry}>
          Reintentar
        </Button>
      }
      {...guidanceNoteErrorColors}
      sx={{ px: 1.5, py: 1.25 }}
    />
  )
}

export function qualifierLabel(resource, number, label) {
  const names = resource === 'products' ? { 2: 'Marca', 3: 'Categoría', 4: 'Tipo' } : {}
  const name = label || names[number]
  return `Característica ${resource === 'products' ? number - 1 : number}${name ? ` (${name})` : ''}`
}

function useOptions(params, enabled = true) {
  const key = JSON.stringify(params)
  const [state, setState] = useState({
    key,
    items: [],
    total: 0,
    page: 0,
    loading: true,
    error: ''
  })
  const request = useRef(null)
  const currentKey = useRef(key)
  currentKey.current = key

  const fetchPage = async (page, token) => {
    try {
      const requestParams = { ...JSON.parse(key), page, per_page: 10 }
      const response = await fetchRbacAssignmentOptions(requestParams)
      if (request.current !== token || currentKey.current !== key) return
      setState((previous) => ({
        ...response,
        key,
        page,
        loading: false,
        error: '',
        items: page === 1 ? response.items : [...previous.items, ...response.items]
      }))
    } catch {
      if (request.current === token && currentKey.current === key)
        setState((previous) => ({
          ...previous,
          loading: false,
          error: 'Revisa tu conexión e intenta de nuevo.'
        }))
    } finally {
      if (request.current === token) request.current = null
    }
  }

  useEffect(() => {
    if (!enabled) return undefined
    const token = {}
    request.current = token
    setState((previous) => ({ ...previous, key, page: 0, loading: true, error: '' }))
    const timer = setTimeout(() => fetchPage(1, token), 250)
    return () => {
      request.current = null
      clearTimeout(timer)
    }
  }, [key, enabled])

  const loadMore = () => {
    if (
      !enabled ||
      request.current ||
      state.key !== key ||
      (state.page > 0 && state.items.length >= state.total)
    )
      return
    const token = {}
    request.current = token
    setState((previous) => ({ ...previous, loading: true, error: '' }))
    fetchPage(state.page + 1, token)
  }
  const onScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
    if (scrollHeight - scrollTop - clientHeight <= 32) loadMore()
  }
  const current = state.key === key
  return {
    ...state,
    items: current ? state.items : [],
    total: current ? state.total : 0,
    page: current ? state.page : 0,
    loading: state.loading || !current,
    onScroll,
    retry: loadMore
  }
}

function qualifierDependencyKey(qualifiers, field) {
  return JSON.stringify(
    Object.entries(qualifiers).filter(([name, values]) => name !== field && values.length)
  )
}

function useQualifierOptions(resource, qualifiers, initialOptions) {
  const request = useRef(null)
  const key = JSON.stringify(qualifiers || {})
  const initialKey = initialOptions && key === '{}' ? key : null
  const [attempt, setAttempt] = useState(0)
  const configuredKeys = useRef(
    new Set(
      Object.entries(initialOptions || {})
        .filter(([, result]) => result.total > 0 || result.items?.length)
        .map(([field]) => field)
    )
  )
  const [state, setState] = useState({
    key: initialKey,
    options: initialOptions || {},
    loading: initialKey === null,
    error: '',
    attempt: 0
  })
  const previousQualifiers = JSON.parse(state.key || '{}')
  const currentQualifiers = JSON.parse(key)
  const fieldChanged = (field) =>
    state.key === null ||
    qualifierDependencyKey(previousQualifiers, field) !==
      qualifierDependencyKey(currentQualifiers, field)
  const needsUpdate = state.key === null || [...configuredKeys.current].some(fieldChanged)

  useEffect(() => {
    if (key !== '{}') return
    if (!initialOptions || !Object.keys(initialOptions).length) return
    if (state.key === '{}' && Object.keys(state.options || {}).length > 0) return

    // Ignore any pending request once the parent supplies the initial options.
    request.current = null

    setState((previous) => {
      const hasOptions = Object.keys(previous.options || {}).length > 0
      if (previous.key === '{}' && hasOptions) return previous

      Object.entries(initialOptions).forEach(([field, result]) => {
        if (result.total > 0 || result.items?.length) configuredKeys.current.add(field)
      })

      return {
        key: '{}',
        options: initialOptions,
        loading: false,
        error: '',
        attempt: previous.attempt
      }
    })
  }, [initialOptions, key])

  useEffect(() => {
    if (!needsUpdate && attempt === state.attempt) {
      setState((previous) => (previous.loading ? { ...previous, loading: false } : previous))
      return undefined
    }
    let cancelled = false
    const token = {}
    request.current = token
    setState((previous) => ({ ...previous, loading: true, error: '' }))
    const timer = setTimeout(() => {
      if (request.current !== token) return
      fetchRbacQualifierOptions(resource, JSON.parse(key))
        .then(({ options }) => {
          if (!options || typeof options !== 'object') throw new Error('Invalid qualifier options')
          if (!cancelled && request.current === token) {
            Object.entries(options).forEach(([field, result]) => {
              if (result.total > 0 || result.items?.length || currentQualifiers[field]?.length)
                configuredKeys.current.add(field)
            })
            setState({ key, options, loading: false, error: '', attempt })
          }
        })
        .catch(() => {
          if (!cancelled && request.current === token)
            setState({
              key,
              options: {},
              loading: false,
              attempt,
              error: 'Revisa tu conexión e intenta de nuevo.'
            })
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [resource, key, attempt])
  return {
    ...state,
    key,
    loading: state.loading || needsUpdate,
    forField: (field) => ({
      ...state,
      key: qualifierDependencyKey(currentQualifiers, field),
      loading: (state.loading || needsUpdate) && fieldChanged(field)
    }),
    retry: () => setAttempt((value) => value + 1)
  }
}

function IndividualSelection({
  resource,
  value,
  initialValue,
  onChange,
  disabled,
  error,
  pinnedUids = []
}) {
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const options = useOptions({ resource, q: appliedSearch })
  const uids = value.uids || []
  const items = value.items || []
  // Existing compact assignments store the unchecked entries. Checkboxes always mean access.
  const excluding = resource !== 'app_users' && value.selectionType === 'exclude'
  const toggle = (item) => {
    const uid = item.uid || item.id
    const stored = uids.includes(uid)
    onChange({
      ...value,
      uids: stored ? uids.filter((entryUid) => entryUid !== uid) : [...uids, uid],
      items: stored
        ? items.filter((entry) => (entry.uid || entry.id) !== uid)
        : [...items, { ...item, uid }]
    })
  }
  const total = options.catalog_total ?? value.total
  const selectedCount = excluding
    ? total == null
      ? null
      : Math.max(0, total - uids.length)
    : uids.length
  // Only assignments present when the editor opened belong at the top.
  // Keep those rows in place even after unchecking or clearing them.
  const initialUids = [...new Set([...(initialValue.uids || []), ...pinnedUids])]
  const pinnedUidSet = new Set(initialUids.map(String))
  const pinnedItems = initialUids.map(
    (uid) =>
      (initialValue.items || []).find((item) => (item.uid || item.id) === uid) ||
      items.find((item) => (item.uid || item.id) === uid) ||
      options.items.find((item) => (item.uid || item.id) === uid) || { uid }
  )
  const orderedItems = [
    ...pinnedItems,
    ...options.items.filter((item) => !pinnedUidSet.has(String(item.uid || item.id)))
  ]
  const visibleItems = appliedSearch.trim() ? options.items : orderedItems
  const isSelected = (item) =>
    excluding ? !uids.includes(item.uid || item.id) : uids.includes(item.uid || item.id)
  const canClear = !disabled && (uids.length > 0 || excluding)
  const clearSelection = () => onChange({ ...value, selectionType: 'include', uids: [], items: [] })
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <RadioGroup
          row
          value={value.all ? 'all' : 'individual'}
          onChange={(_, mode) => {
            // Starting from all keeps every checkbox checked without loading the full catalog.
            if (mode === 'individual' && value.all && resource !== 'app_users') {
              onChange({ ...value, all: false, selectionType: 'exclude', uids: [], items: [] })
            } else {
              onChange({ ...value, all: mode === 'all' })
            }
          }}
        >
          <FormControlLabel
            disabled={disabled}
            value="individual"
            control={<Radio size="small" />}
            label="Selección individual"
          />
          <FormControlLabel
            disabled={disabled}
            value="all"
            control={<Radio size="small" />}
            label={
              resource === 'app_users'
                ? 'Todos los usuarios'
                : resource === 'products'
                  ? 'Todos los productos'
                  : 'Todos los clientes'
            }
          />
        </RadioGroup>
      </Stack>
      {value.all && (
        <GuidanceNote
          icon={<InfoOutlinedIcon />}
          title={null}
          message={`Tendrá acceso a todos los ${resource === 'app_users' ? 'usuarios de Ubiqua App' : resource === 'products' ? 'productos' : 'clientes'} de este catálogo.`}
          accentColor={UI_COLOR_PRIMARY_HOVER}
          backgroundColor={UI_COLOR_SURFACE_INFO}
          textColor={UI_COLOR_TEXT_INFO}
          singleLine
        />
      )}
      {!value.all && (
        <>
          <RbacAssignmentSearch
            disabled={disabled}
            error={error}
            describedBy={error ? `${resource}-selection-error` : undefined}
            placeholder={
              resource === 'app_users'
                ? 'Buscar usuario de Ubiqua App...'
                : resource === 'products'
                  ? 'Buscar producto por código o nombre...'
                  : 'Buscar cliente por código o nombre...'
            }
            value={searchInput}
            onChange={(text) => {
              setSearchInput(text)
              if (!text) setAppliedSearch('')
            }}
            onSearch={() => setAppliedSearch(searchInput)}
          />
          {error && (
            <Typography
              id={`${resource}-selection-error`}
              variant="caption"
              sx={{ color: UI_COLOR_DANGER }}
            >
              Selecciona una o más opciones.
            </Typography>
          )}
          {options.error && (
            <AssignmentRetryNote
              message={options.error}
              onRetry={options.retry}
              disabled={disabled || options.loading}
            />
          )}
          <Box
            role="group"
            aria-label={TITLES[resource]}
            aria-invalid={error || undefined}
            aria-describedby={error ? `${resource}-selection-error` : undefined}
            sx={{
              border: '1px solid',
              borderColor: error ? UI_COLOR_DANGER : '#dce6f0',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
            aria-busy={options.loading}
          >
            {canClear && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                sx={{
                  px: 1.5,
                  minHeight: 44,
                  bgcolor: '#f7f9fc',
                  borderBottom: '1px solid #eaf0f6',
                  gap: 1
                }}
              >
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  onClick={clearSelection}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      clearSelection()
                    }
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0,
                    p: 0,
                    m: 0,
                    border: 0,
                    bgcolor: 'transparent',
                    boxShadow: 'none',
                    color: UI_COLOR_SURFACE_600,
                    fontSize: 13,
                    fontWeight: 400,
                    lineHeight: 'normal',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    '& svg': { display: 'block', flexShrink: 0 },
                    '&:hover': { color: UI_COLOR_DANGER }
                  }}
                >
                  <Broom size={15} weight="regular" aria-hidden="true" />
                  <Box component="span">Limpiar selección</Box>
                </Box>
              </Stack>
            )}
            <Box
              className="rbac-assignment-list"
              onScroll={options.onScroll}
              sx={{ maxHeight: 240, minHeight: 100, overflowY: 'auto', px: 0.75, pb: 0.75 }}
            >
              {options.loading && options.page === 0 ? (
                <Box
                  sx={{
                    minHeight: 100,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <LoadingSpinner text="Cargando..." />
                </Box>
              ) : (
                visibleItems.map((item) => {
                  const uid = item.uid || item.id
                  return (
                    <FormControlLabel
                      key={uid}
                      sx={{
                        display: 'flex',
                        m: 0,
                        p: 0.5,
                        borderRadius: 2,
                        alignItems: 'center',
                        bgcolor: isSelected(item) ? '#eef4fc' : 'transparent',
                        '&:hover': { bgcolor: '#f2f6fb' },
                        '& .MuiFormControlLabel-label': { minWidth: 0, py: 0.5 }
                      }}
                      control={
                        <Checkbox
                          disabled={disabled || options.page === 0}
                          size="small"
                          inputProps={{ 'aria-label': item.name ? `${uid} - ${item.name}` : uid }}
                          checked={isSelected(item)}
                          onChange={() => toggle(item)}
                        />
                      }
                      label={
                        resource === 'app_users' ? (
                          <Typography
                            variant="body2"
                            title={item.name ? `${uid} - ${item.name}` : uid}
                            sx={{
                              color: UI_COLOR_SURFACE_900,
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {item.name ? `${uid} - ${item.name}` : uid}
                          </Typography>
                        ) : (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: UI_COLOR_SURFACE_900,
                                fontWeight: 500,
                                overflowWrap: 'anywhere'
                              }}
                            >
                              {item.name || uid}
                            </Typography>
                            {item.name && (
                              <Typography
                                variant="caption"
                                sx={{ color: UI_COLOR_SURFACE_600, overflowWrap: 'anywhere' }}
                              >
                                {uid}
                              </Typography>
                            )}
                          </Box>
                        )
                      }
                    />
                  )
                })
              )}
              {options.loading && options.page > 0 && (
                <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'center' }}>
                  <LoadingSpinner text="Actualizando..." />
                </Box>
              )}
              {!options.loading && !options.error && visibleItems.length === 0 && (
                <Stack
                  alignItems="center"
                  spacing={0.5}
                  sx={{ py: 3, color: UI_COLOR_SURFACE_600 }}
                >
                  <SearchIcon />
                  <Typography variant="body2">No hay resultados.</Typography>
                  <Typography variant="caption">Prueba con otro nombre o código.</Typography>
                </Stack>
              )}
            </Box>
          </Box>
          <Typography variant="caption" color={UI_COLOR_SURFACE_600}>
            {selectedCount == null
              ? options.loading
                ? 'Cargando selección...'
                : 'El total de seleccionados no está disponible.'
              : selectedCount
                ? `${selectedCount} seleccionado(s).`
                : resource === 'app_users'
                  ? 'Ningún usuario seleccionado todavía. Solo verá su actividad propia.'
                  : `Ningún ${resource === 'products' ? 'producto' : 'cliente'} seleccionado todavía.`}
          </Typography>
        </>
      )}
    </Stack>
  )
}

function QualifierSelection({
  resource,
  number,
  value,
  onChange,
  disabled,
  checking,
  onInitialStateChange,
  error,
  batch
}) {
  const [search, setSearch] = useState('')
  const [popupOpen, setPopupOpen] = useState(false)
  const initialLoadComplete = useRef(false)
  const hadAvailableData = useRef(false)
  const pageRequest = useRef(null)
  const key = qualifierKey(number)
  const dependencies = Object.fromEntries(
    Object.entries(value.qualifiers || {}).filter(([name]) => name !== key)
  )
  const searching = Boolean(search.trim())
  const searchedOptions = useOptions(
    { resource, qualifier: key, qualifiers: dependencies, q: search },
    searching
  )
  const [more, setMore] = useState({ key: '', items: [], page: 1, loading: false, error: '' })
  const currentMore =
    more.key === batch.key ? more : { items: [], page: 1, loading: false, error: '' }
  const firstPage = batch.options[key] || { items: [], total: 0, page: 0 }
  const loadMore = () => {
    if (
      batch.loading ||
      currentMore.loading ||
      pageRequest.current === batch.key ||
      currentMore.items.length + firstPage.items.length >= firstPage.total
    )
      return
    const page = currentMore.page + 1
    pageRequest.current = batch.key
    setMore({ ...currentMore, key: batch.key, loading: true, error: '' })
    fetchRbacAssignmentOptions({
      resource,
      qualifier: key,
      qualifiers: dependencies,
      page,
      per_page: 10
    })
      .then((response) =>
        setMore((previous) =>
          previous.key === batch.key
            ? { ...previous, items: [...previous.items, ...response.items], page, loading: false }
            : previous
        )
      )
      .catch(() =>
        setMore((previous) =>
          previous.key === batch.key
            ? { ...previous, loading: false, error: 'Revisa tu conexión e intenta de nuevo.' }
            : previous
        )
      )
      .finally(() => {
        if (pageRequest.current === batch.key) pageRequest.current = null
      })
  }
  const onScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
    if (scrollHeight - scrollTop - clientHeight <= 32) loadMore()
  }
  const options = searching
    ? searchedOptions
    : {
        ...firstPage,
        items: [...firstPage.items, ...currentMore.items],
        page: batch.loading ? 0 : currentMore.page,
        loading: batch.loading || currentMore.loading,
        error: currentMore.error || batch.error,
        onScroll,
        retry: loadMore
      }
  const selected = value.qualifiers[key] || []
  const availableOptions = [...new Set([...selected, ...options.items])]
  const label = qualifierLabel(resource, number, options.label)
  const labelParts = label.match(/^(Característica \d+)(.*)$/)
  const refreshing = options.loading || checking
  const hasAvailableData = selected.length > 0 || options.items.length > 0 || options.total > 0
  if (!refreshing && !search.trim()) hadAvailableData.current = hasAvailableData
  const knownAvailableData = hasAvailableData || (refreshing && hadAvailableData.current)
  const showLoading = refreshing && initialLoadComplete.current && hadAvailableData.current
  const showDropdownLoading =
    popupOpen && !availableOptions.some((option) => !selected.includes(option))

  useEffect(() => {
    const loading = !initialLoadComplete.current && options.loading && options.page === 0
    if (!options.loading && (options.page > 0 || options.error)) initialLoadComplete.current = true
    onInitialStateChange(key, {
      loading,
      available: selected.length > 0 || options.items.length > 0 || options.total > 0,
      error: Boolean(options.error)
    })
  }, [
    key,
    onInitialStateChange,
    options.error,
    options.items.length,
    options.loading,
    options.page,
    options.total,
    selected.length
  ])

  if (!search.trim() && !options.error && !knownAvailableData) return null

  return (
    <Box aria-busy={refreshing}>
      <Typography
        component="label"
        htmlFor={`${resource}-${key}`}
        variant="body2"
        color={UI_COLOR_SURFACE_600}
        sx={{ display: 'block', mb: 0.75 }}
      >
        <Box component="span" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
          {labelParts?.[1] || label}:
        </Box>
        {labelParts?.[2] && <Box component="span">{labelParts[2]}</Box>}
      </Typography>
      <Autocomplete
        className="rbac-qualifier-select"
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        disabled={disabled}
        options={availableOptions}
        value={selected}
        filterOptions={(entries) => entries}
        inputValue={search}
        loading={showLoading}
        onOpen={() => setPopupOpen(true)}
        onClose={() => setPopupOpen(false)}
        getOptionDisabled={(option) => options.page === 0 && !selected.includes(option)}
        slotProps={{
          listbox: {
            onScroll: options.onScroll,
            sx: {
              maxHeight: 240,
              p: 0.75,
              '& .MuiAutocomplete-option': { borderRadius: 2, py: 1, fontSize: 13 }
            }
          },
          paper: {
            sx: {
              mt: 0.75,
              border: '1px solid #dce6f0',
              borderRadius: 3,
              boxShadow: '0 12px 32px rgba(39, 88, 141, 0.14)'
            }
          }
        }}
        loadingText={<LoadingSpinner text="Cargando..." size={14} />}
        noOptionsText="No hay resultados"
        clearText="Limpiar selección"
        clearIcon={<Broom size={16} />}
        openText="Ver opciones"
        closeText="Cerrar opciones"
        renderOption={(props, option, { selected: checked }) => {
          const { key: optionKey, ...optionProps } = props
          return (
            <li key={optionKey ?? option} {...optionProps}>
              <Checkbox
                tabIndex={-1}
                disableRipple
                checked={checked}
                size="small"
                sx={{
                  p: 0,
                  mr: 1,
                  color: UI_COLOR_SECONDARY_MID,
                  '&.Mui-checked': { color: UI_COLOR_SECONDARY_MAIN },
                  '& svg': { m: 0 }
                }}
              />
              <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
                {option}
              </Box>
            </li>
          )
        }}
        onInputChange={(_, text, reason) => {
          if (reason === 'input' || reason === 'clear') setSearch(text)
        }}
        onChange={(_, values) => {
          setSearch('')
          onChange({ ...value, qualifiers: { ...value.qualifiers, [key]: values } })
        }}
        renderTags={(values, getTagProps) =>
          values.map((option, index) => {
            const { key: tagKey, ...tagProps } = getTagProps({ index })
            return (
              <AppChip
                {...tagProps}
                key={tagKey ?? option}
                label={option}
                variant="selection"
                sx={{
                  minWidth: '0 !important',
                  padding: '0 6px 0 8px !important',
                  gap: '4px',
                  color: UI_COLOR_RBAC_ROLE_TEXT,
                  backgroundColor: UI_COLOR_RBAC_ROLE_BG,
                  fontSize: '13px !important',
                  fontWeight: '600 !important',
                  textTransform: 'none !important',
                  flexShrink: 0,
                  '& .MuiChip-deleteIcon': {
                    m: '0 !important',
                    color: `${UI_COLOR_RBAC_ROLE_TEXT} !important`,
                    fontSize: 16,
                    opacity: 0.7,
                    flexShrink: 0
                  }
                }}
              />
            )
          })
        }
        sx={{
          '& .MuiOutlinedInput-root': {
            minHeight: 52,
            flexWrap: 'wrap',
            pr: '100px !important',
            py: 0.75
          },
          '& .MuiAutocomplete-input': { minWidth: '80px !important' },
          '& .MuiAutocomplete-endAdornment': { right: 9 },
          '& .MuiAutocomplete-clearIndicator': {
            color: UI_COLOR_SURFACE_600,
            backgroundColor: 'transparent !important'
          },
          '& .MuiAutocomplete-clearIndicator:hover': {
            color: UI_COLOR_DANGER,
            backgroundColor: 'transparent !important'
          },
          '& .MuiAutocomplete-clearIndicator.Mui-disabled': { color: UI_COLOR_SURFACE_600 },
          '& .MuiChip-root': { maxWidth: '100%' },
          '&.Mui-disabled': { cursor: 'not-allowed' }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            error={error}
            helperText={error ? 'Selecciona una o más opciones.' : undefined}
            id={`${resource}-${key}`}
            placeholder={
              selected.length === 0 ? 'Busque o seleccione la característica...' : undefined
            }
            inputProps={{ ...params.inputProps, 'aria-label': label }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {selected.length === 0 && (
                    <SearchIcon sx={{ color: UI_COLOR_SURFACE_600, mr: 1, flexShrink: 0 }} />
                  )}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {showLoading && !showDropdownLoading && (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 42,
                        px: 1,
                        bgcolor: 'background.paper',
                        zIndex: 1,
                        '& .MuiTypography-root': { fontSize: 12, fontWeight: 500 },
                        '& [role="status"]': { gap: 0.75 },
                        '& [role="status"] > span:first-of-type': {
                          boxShadow: 'none',
                          borderWidth: 2
                        }
                      }}
                    >
                      <LoadingSpinner text="Cargando..." size={16} />
                    </Box>
                  )}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
      {options.error && !batch.error && (
        <AssignmentRetryNote
          message={options.error}
          onRetry={options.retry}
          disabled={disabled || options.loading}
        />
      )}
    </Box>
  )
}

function QualifierSelections({
  resource,
  value,
  onChange,
  disabled,
  onValidationChange,
  onAvailabilityChange,
  initialOptions,
  selectionError
}) {
  const { checking, error, retry } = useQualifierReconciliation(resource, value, onChange)
  const batch = useQualifierOptions(resource, value.qualifiers, initialOptions)
  const [initialStates, setInitialStates] = useState(() =>
    Object.fromEntries(
      QUALIFIER_NUMBERS.map((number) => [
        qualifierKey(number),
        { loading: true, available: false, error: false }
      ])
    )
  )
  const updateInitialState = useCallback((key, state) => {
    setInitialStates((previous) => {
      const current = previous[key]
      return current.loading === state.loading &&
        current.available === state.available &&
        current.error === state.error
        ? previous
        : { ...previous, [key]: state }
    })
  }, [])
  const initialValues = Object.values(initialStates)
  const initialStatus = batch.loading
    ? 'loading'
    : batch.error
      ? 'error'
      : initialValues.some((state) => state.loading)
        ? 'loading'
        : initialValues.some((state) => state.available)
          ? 'available'
          : initialValues.some((state) => state.error)
            ? 'error'
            : 'unavailable'
  const validationPending =
    checking.length > 0 || Boolean(error) || batch.loading || Boolean(batch.error)
  useEffect(() => {
    onValidationChange(resource, validationPending)
    return () => onValidationChange(resource, false)
  }, [resource, validationPending, onValidationChange])
  useEffect(() => {
    onAvailabilityChange(resource, initialStatus)
    if (initialStatus === 'unavailable') onChange({ ...value, strategy: 'individual' })
  }, [initialStatus])

  return (
    <Stack spacing={2}>
      <Box>
        <Stack spacing={2}>
          <Typography variant="caption" color={UI_COLOR_SURFACE_600}>
            Puedes combinar varias características. Tus selecciones se conservan mientras
            actualizamos las opciones relacionadas.
          </Typography>
          {error && (
            <AssignmentRetryNote
              title="No se pudieron comprobar las selecciones"
              message={error}
              onRetry={retry}
              disabled={disabled}
            />
          )}
          {batch.error && (
            <AssignmentRetryNote
              message={batch.error}
              onRetry={batch.retry}
              disabled={disabled || batch.loading}
            />
          )}
          {QUALIFIER_NUMBERS.map((number) => (
            <QualifierSelection
              key={number}
              resource={resource}
              number={number}
              value={value}
              onChange={onChange}
              disabled={disabled}
              checking={checking.includes(qualifierKey(number))}
              onInitialStateChange={updateInitialState}
              error={selectionError}
              batch={batch.forField(qualifierKey(number))}
            />
          ))}
        </Stack>
      </Box>
    </Stack>
  )
}

function AssignmentStrategyOptions({ resource, selected, update, disabled, availability }) {
  const qualifiersDisabled = disabled || ['loading', 'unavailable', 'error'].includes(availability)
  const qualifierTooltip = disabled
    ? ''
    : availability === 'loading'
      ? 'Comprobando las características disponibles…'
      : availability === 'error'
        ? 'No pudimos comprobar las características disponibles.'
        : availability === 'unavailable'
          ? `No hay características de ${resource === 'products' ? 'productos' : 'clientes'} configuradas para este negocio.`
          : ''

  return (
    <RadioGroup
      aria-label={`Tipo de asignación de ${resource === 'products' ? 'productos' : 'clientes'}`}
      value={selected.strategy}
      onChange={(_, strategy) => update({ ...selected, strategy })}
      sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.25, mb: 1.5 }}
    >
      {['individual', 'qualifiers'].map((strategy) => {
        const isQualifiers = strategy === 'qualifiers'
        const option = (
          <FormControlLabel
            key={strategy}
            disabled={isQualifiers ? qualifiersDisabled : disabled}
            value={strategy}
            control={<Radio size="small" disableRipple sx={{ alignSelf: 'flex-start' }} />}
            sx={{
              width: '100%',
              boxSizing: 'border-box',
              m: 0,
              p: 1,
              alignItems: 'flex-start',
              border: '1px solid',
              borderColor: selected.strategy === strategy ? '#aac5e5' : '#e1e6eb',
              borderRadius: '12px',
              backgroundColor: selected.strategy === strategy ? '#f2f7fd' : 'transparent',
              transition: 'background-color 160ms, border-color 160ms',
              '&:hover': {
                borderColor: isQualifiers && qualifiersDisabled ? '#e1e6eb' : UI_COLOR_SECONDARY_MID
              }
            }}
            label={
              <Box sx={{ py: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" fontWeight={600} color={UI_COLOR_SURFACE_900}>
                    {isQualifiers
                      ? 'Por características'
                      : resource === 'products'
                        ? 'Por producto'
                        : 'Por cliente'}
                  </Typography>
                </Stack>
                <Typography variant="caption" color={UI_COLOR_SURFACE_600}>
                  {isQualifiers
                    ? 'Selecciona según las características configuradas por el negocio.'
                    : `Selecciona ${resource === 'products' ? 'productos' : 'clientes'} específicos, uno por uno.`}
                </Typography>
              </Box>
            }
          />
        )

        return isQualifiers ? (
          <Tooltip
            key={strategy}
            title={qualifierTooltip}
            placement="top"
            arrow
            disableHoverListener={!qualifierTooltip}
          >
            <Box component="span" sx={{ display: 'block', width: '100%' }}>
              {option}
            </Box>
          </Tooltip>
        ) : (
          option
        )
      })}
    </RadioGroup>
  )
}

export default function RbacAssignedDataEditor({
  value,
  pinnedUids = [],
  onChange,
  disabled = false,
  onValidationChange,
  showValidation = false,
  resources = ASSIGNED_RESOURCES
}) {
  const data = { ...emptyAssignedData(), ...value }
  const [initialSelections] = useState(() => data)
  const requiresSelection = resources.some((resource) => resource !== 'app_users')
  const selectionError = showValidation && requiresSelection && !hasAnyAssignedData(data, resources)
  const latestData = useRef(data)
  latestData.current = data
  const [validations, setValidations] = useState({})
  const [initialQualifierOptions, setInitialQualifierOptions] = useState({})
  const [qualifierAvailability, setQualifierAvailability] = useState(() =>
    Object.fromEntries(
      ['products', 'clients'].map((resource) => [
        resource,
        Object.values(data[resource].qualifiers || {}).some((values) => values.length > 0)
          ? 'available'
          : 'loading'
      ])
    )
  )
  const updateValidation = useCallback((resource, pending) => {
    setValidations((previous) =>
      previous[resource] === pending ? previous : { ...previous, [resource]: pending }
    )
  }, [])
  const validationPending = Object.values(validations).some(Boolean)
  const updateQualifierAvailability = useCallback((resource, status) => {
    setQualifierAvailability((previous) =>
      previous[resource] === status ? previous : { ...previous, [resource]: status }
    )
  }, [])
  const resourceKey = resources.join(',')
  useEffect(() => {
    let cancelled = false
    resourceKey
      .split(',')
      .filter((resource) => ['products', 'clients'].includes(resource))
      .forEach((resource) => {
        fetchRbacQualifierOptions(resource, {})
          .then(({ options }) => {
            if (!options || typeof options !== 'object')
              throw new Error('Invalid qualifier options')
            if (cancelled) return
            setInitialQualifierOptions((previous) => ({ ...previous, [resource]: options }))
            const available = Object.values(options).some(
              (result) => result.total > 0 || result.items?.length
            )
            updateQualifierAvailability(resource, available ? 'available' : 'unavailable')
          })
          .catch(() => {
            if (!cancelled) updateQualifierAvailability(resource, 'error')
          })
      })
    return () => {
      cancelled = true
    }
  }, [resourceKey, updateQualifierAvailability])
  useEffect(() => {
    onValidationChange?.(validationPending)
    return () => onValidationChange?.(false)
  }, [validationPending, onValidationChange])
  return (
    <Stack
      className="rbac-assignment-editor"
      spacing={2}
      sx={{
        '& .MuiCheckbox-root .MuiSvgIcon-root, & .MuiRadio-root .MuiSvgIcon-root': { mx: 0 },
        '& .MuiRadio-root:not(.Mui-disabled), & .MuiCheckbox-root:not(.Mui-disabled)': {
          color: UI_COLOR_SECONDARY_MID
        },
        '& .MuiRadio-root:hover, & .MuiCheckbox-root:hover': { backgroundColor: 'transparent' },
        '& .MuiRadio-root.Mui-checked:not(.Mui-disabled), & .MuiCheckbox-root.Mui-checked:not(.Mui-disabled)':
          { color: UI_COLOR_SECONDARY_MAIN }
      }}
    >
      {selectionError && (
        <GuidanceNote
          icon={<ErrorOutlineOutlinedIcon />}
          title={null}
          message="Selecciona al menos un producto, cliente o usuario de Ubiqua App, o asigna acceso a todos."
          {...guidanceNoteErrorColors}
          singleLine
          role="alert"
          messageSx={{ fontSize: 13 }}
        />
      )}
      {resources.map((resource) => {
        const selected = data[resource]
        const update = (next) => {
          latestData.current = { ...latestData.current, [resource]: next }
          onChange(latestData.current)
        }
        const Icon = RESOURCE_ICONS[resource]
        return (
          <RbacAssignedDataCard
            key={resource}
            title={TITLES[resource]}
            icon={<Icon size={20} />}
            error={selectionError}
          >
            <Typography variant="body2" color={UI_COLOR_SURFACE_600} sx={{ mb: 1.5 }}>
              {resource === 'app_users'
                ? 'Selecciona los usuarios de Ubiqua App cuya actividad podrá ver este usuario. Si no eliges a ninguno, solo podrá ver la actividad que genere.'
                : `Define qué parte del catálogo de ${resource === 'products' ? 'productos' : 'clientes'} podrá ver y usar este usuario en Torre.`}
            </Typography>

            {resource !== 'app_users' && (
              <AssignmentStrategyOptions
                resource={resource}
                selected={selected}
                update={update}
                disabled={disabled}
                availability={qualifierAvailability[resource]}
              />
            )}
            {selected.strategy === 'qualifiers' && resource !== 'app_users' ? (
              <Box sx={{ mt: 2 }}>
                <QualifierSelections
                  resource={resource}
                  value={selected}
                  onChange={update}
                  disabled={disabled}
                  onValidationChange={updateValidation}
                  onAvailabilityChange={updateQualifierAvailability}
                  initialOptions={initialQualifierOptions[resource]}
                  selectionError={selectionError}
                />
              </Box>
            ) : (
              <IndividualSelection
                resource={resource}
                value={selected}
                initialValue={initialSelections[resource]}
                pinnedUids={resource === 'app_users' ? pinnedUids : []}
                onChange={update}
                disabled={disabled}
                error={selectionError}
              />
            )}
          </RbacAssignedDataCard>
        )
      })}
    </Stack>
  )
}
